import { useRef, useEffect, useCallback, useState } from 'react'
import type { Editor } from '@tiptap/react'

interface UseEditorContentSyncProps {
  content?: string
  onChange?: (content: string) => void
}

/**
 * Hook to manage bidirectional content synchronization between editor and external state
 * Handles updates from both the editor (via onUpdate) and external content changes
 */
export const useEditorContentSync = ({
  content,
  onChange,
}: UseEditorContentSyncProps) => {
  const isSyncingContentRef = useRef(false)
  const [currentEditor, setCurrentEditor] = useState<Editor | null>(null)

  const handleUpdate = useCallback(
    ({ editor: updatedEditor }: { editor: Editor }) => {
      if (isSyncingContentRef.current) {
        isSyncingContentRef.current = false
        return
      }

      const markdownStorage = (updatedEditor.storage as { markdown?: { getMarkdown: () => string } }).markdown
      const markdown = markdownStorage?.getMarkdown() ?? ''
      onChange?.(markdown)
    },
    [onChange]
  )

  // Sync external content changes to editor
  useEffect(() => {
    const editor = currentEditor
    if (!editor) return
    const markdownStorage = (editor.storage as { markdown?: { getMarkdown: () => string } }).markdown
    const currentMarkdown = markdownStorage?.getMarkdown() ?? ''
    const nextContent = content ?? ''
    if (currentMarkdown !== nextContent) {
      isSyncingContentRef.current = true
      editor.commands.setContent(nextContent)
    }
  }, [currentEditor, content])

  const registerEditor = useCallback((editor: Editor | null) => {
    setCurrentEditor(editor)
  }, [])

  return { handleUpdate, registerEditor }
}
